/**
 * QuizModule - Colagea Hair Regrowth Quiz v6
 * 
 * CRO-aggressive hair loss assessment quiz with:
 * - Broad opening question (severity) to ease users in
 * - Progressive detail questions (duration, shedding, area, texture)
 * - Lifestyle & previous treatment questions
 * - Psychological belief checkpoints with urgency triggers
 * - IN-QUIZ result page with danger score, personalized treatment plan, and CTA
 * - No redirect - results shown directly inside the quiz modal
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { 
  ChevronRight, ChevronLeft, Loader2, CheckCircle2, AlertTriangle, 
  Clock, Package, Shield, Lock, Droplets, Scissors, TrendingDown, Zap,
  ShieldCheck, Truck, RotateCcw, Star, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { trackQuizProgress, trackQuizEmailCapture, trackQuizComplete } from '@/lib/ga4-tracking';
import TrustpilotStars from "@/components/TrustpilotStars";

// ============================================
// TESTIMONIALS DATA (categorized for hair loss)
// ============================================
interface Testimonial {
  name: string;
  text: string;
  tags: string[];
}

const testimonials: Testimonial[] = [
  { 
    name: "Marcus D., 42", 
    text: "I noticed my crown thinning at 38. Tried minoxidil for 2 years — nothing. 6 weeks with Colagea and my barber asked what I changed. Actual visible regrowth.",
    tags: ['crown-thinning', 'tried-minoxidil', 'moderate-loss']
  },
  { 
    name: "James R., 51", 
    text: "My temples had been receding since my 30s. I'd accepted it. My wife ordered Colagea for me — 8 weeks later, I have baby hairs growing where there was nothing.",
    tags: ['receding-hairline', 'temples', 'long-duration', 'severe-loss']
  },
  { 
    name: "Kevin L., 35", 
    text: "I was losing hair in the shower drain every day. After one month of Colagea, the shedding stopped completely. Now at month 3, I see real density coming back.",
    tags: ['excessive-shedding', 'early-stage', 'moderate-loss']
  },
  { 
    name: "Daniel P., 47", 
    text: "Spent $4,000+ on PRP treatments. Results lasted 3 months. Colagea costs a fraction and the results keep improving. I'm furious I didn't find this sooner.",
    tags: ['tried-prp', 'tried-expensive', 'crown-thinning']
  },
  { 
    name: "Robert K., 55", 
    text: "At 55, I thought it was too late. The micro-needle delivery actually works — I can feel the difference in my scalp. 10 weeks in, my hair is visibly thicker.",
    tags: ['age-50plus', 'severe-loss', 'long-duration']
  },
  { 
    name: "Chris M., 39", 
    text: "Finasteride gave me side effects I couldn't live with. Colagea has zero side effects and honestly better results. My confidence is back.",
    tags: ['tried-finasteride', 'side-effects', 'moderate-loss']
  },
  { 
    name: "Andrew S., 44", 
    text: "Diffuse thinning all over. Every product just sat on top of my scalp. The micro-infusion is different — you can actually feel it working at the root.",
    tags: ['diffuse-thinning', 'tried-serums', 'moderate-loss']
  },
  { 
    name: "Thomas H., 33", 
    text: "Started losing hair at 28. Tried everything OTC. Colagea is the first thing that actually penetrates deep enough to make a difference. 90 days — real results.",
    tags: ['early-onset', 'tried-otc', 'early-stage']
  },
  { 
    name: "William B., 48", 
    text: "My dermatologist was skeptical when I told him about Colagea. After seeing my 3-month results, he's now recommending it to other patients.",
    tags: ['dermatologist', 'crown-thinning', 'moderate-loss']
  },
  { 
    name: "Steven G., 37", 
    text: "Stress-related hair loss from a divorce. I was shedding clumps. 5 weeks of Colagea and the shedding stopped. 3 months later, filling back in nicely.",
    tags: ['stress-related', 'excessive-shedding', 'lifestyle']
  },
  { 
    name: "Patrick N., 52", 
    text: "I've been using hair fibers to hide my bald spot for years. After 2 months of Colagea, I don't need them anymore. That's all you need to know.",
    tags: ['bald-spot', 'severe-loss', 'long-duration']
  },
  { 
    name: "Michael T., 41", 
    text: "Fine, thin hair my whole life. Colagea didn't just stop the loss — it made my existing hair thicker and stronger. My hair has never looked this good.",
    tags: ['fine-hair', 'thin-texture', 'moderate-loss']
  },
];

// ============================================
// TAG LABELS for results page (max 4 shown)
// ============================================
interface TagLabel {
  tags: string[];
  label: string;
  priority: number;
}

const tagLabels: TagLabel[] = [
  { tags: ['crown-thinning'], label: 'Crown Restoration', priority: 100 },
  { tags: ['receding-hairline'], label: 'Hairline Recovery', priority: 99 },
  { tags: ['diffuse-thinning'], label: 'Full Coverage Treatment', priority: 98 },
  { tags: ['temples'], label: 'Temple Regrowth', priority: 97 },
  { tags: ['excessive-shedding'], label: 'Anti-Shedding Formula', priority: 96 },
  { tags: ['severe-loss'], label: 'Intensive Recovery', priority: 95 },
  { tags: ['moderate-loss'], label: 'Active Regrowth', priority: 90 },
  { tags: ['early-stage'], label: 'Early Intervention', priority: 88 },
  { tags: ['fine-hair'], label: 'Fine Hair Thickening', priority: 87 },
  { tags: ['thin-texture'], label: 'Density Booster', priority: 86 },
  { tags: ['coarse-hair'], label: 'Deep Penetration Formula', priority: 85 },
  { tags: ['curly-hair'], label: 'Curl-Safe Treatment', priority: 84 },
  { tags: ['tried-minoxidil'], label: 'Minoxidil Alternative', priority: 83 },
  { tags: ['tried-finasteride'], label: 'No Side Effects', priority: 82 },
  { tags: ['tried-nothing'], label: 'First-Time Treatment', priority: 80 },
  { tags: ['tried-expensive'], label: 'Cost-Effective Solution', priority: 79 },
  { tags: ['urgent'], label: 'Urgent Treatment', priority: 92 },
  { tags: ['rapid-loss'], label: 'Rapid Response Needed', priority: 91 },
  { tags: ['long-duration'], label: 'Deep Follicle Revival', priority: 78 },
  { tags: ['stress-related'], label: 'Stress Recovery', priority: 77 },
  { tags: ['genetic'], label: 'Genetic Pattern Treatment', priority: 76 },
  { tags: ['lifestyle'], label: 'Lifestyle Optimized', priority: 70 },
];

function getDisplayLabels(quizTags: string[]): string[] {
  const matchedLabels: { label: string; priority: number }[] = [];
  
  for (const tagLabel of tagLabels) {
    const hasMatch = tagLabel.tags.some(tag => quizTags.includes(tag));
    if (hasMatch && !matchedLabels.find(m => m.label === tagLabel.label)) {
      matchedLabels.push({ label: tagLabel.label, priority: tagLabel.priority });
    }
  }
  
  return matchedLabels
    .sort((a, b) => b.priority - a.priority)
    .slice(0, 4)
    .map(m => m.label);
}

// ============================================
// QUESTION TYPES AND DATA
// ============================================
type QuestionType = 'single' | 'multiple' | 'checkpoint';

interface QuizQuestion {
  id: string;
  type: QuestionType;
  question: string;
  subtitle?: string;
  icon?: React.ReactNode;
  options: {
    id: string;
    label: string;
    description?: string;
    emoji?: string;
    value: number;
    tags?: string[];
  }[];
}

// Belief checkpoints triggered after specific questions
interface BeliefCheckpoint {
  triggeredAfter: string;
  getMessage: (answers: Record<string, string[]>) => { title: string; body: string; stat?: string } | null;
}

const beliefCheckpoints: BeliefCheckpoint[] = [
  {
    triggeredAfter: 'loss-duration',
    getMessage: (answers) => {
      const duration = answers['loss-duration']?.[0];
      if (duration === '3-5-years' || duration === '5-plus' || duration === 'over-10') {
        const years = duration === '5-plus' ? '5+' : duration === 'over-10' ? '10+' : '3-5';
        return {
          title: `You've been losing hair for ${years} years...`,
          body: "Every month without treatment, dormant follicles become harder to reactivate. The good news? Clinical studies show that even follicles inactive for 5+ years can be revived with deep micro-infusion delivery.",
          stat: "Follicles left untreated for 7+ years have a 40% lower reactivation rate"
        };
      }
      return null;
    }
  },
  {
    triggeredAfter: 'previous-treatments',
    getMessage: (answers) => {
      const treatments = answers['previous-treatments'] || [];
      if (treatments.includes('minoxidil') || treatments.includes('finasteride') || treatments.includes('prp')) {
        return {
          title: "Here's why those treatments didn't work for you",
          body: "Minoxidil only works on surface blood flow. Finasteride blocks hormones systemically with side effects. PRP is expensive and temporary. None of them deliver active compounds directly to the follicle root.",
          stat: "95% of topical treatments never penetrate past the scalp's outer layer"
        };
      }
      if (treatments.includes('nothing')) {
        return {
          title: "You haven't tried anything yet. That's actually good news.",
          body: "Your follicles haven't been exposed to harsh chemicals or hormonal treatments. This means they're more responsive to targeted activation. Starting with Colagea's micro-infusion gives you the best possible first-treatment results.",
          stat: "First-time users see 23% faster results than those switching from other treatments"
        };
      }
      return null;
    }
  },
  {
    triggeredAfter: 'shedding-rate',
    getMessage: (answers) => {
      const shedding = answers['shedding-rate']?.[0];
      if (shedding === 'heavy' || shedding === 'clumps') {
        return {
          title: "Your shedding rate is above normal",
          body: "Losing more than 100 hairs per day signals active follicle miniaturization. This is your body telling you that without intervention, the loss will accelerate. The sooner you act, the more follicles can be saved.",
          stat: "Active shedding can lead to 30% permanent follicle loss within 12 months if untreated"
        };
      }
      return null;
    }
  }
];

// ============================================
// QUIZ QUESTIONS - Reordered for CRO
// Start broad (severity) → build urgency → get specific later
// ============================================
const quizQuestions: QuizQuestion[] = [
  // Q1: BROAD opener - severity (low barrier, everyone can answer)
  {
    id: 'loss-severity',
    type: 'single',
    question: 'How would you rate your hair loss?',
    subtitle: 'Be honest. This helps us recommend the right treatment plan.',
    icon: <AlertTriangle className="w-6 h-6" />,
    options: [
      { id: 'early', label: 'Early stage', emoji: '🟡', description: 'Just starting to notice thinning', value: 4, tags: ['early-stage'] },
      { id: 'moderate', label: 'Moderate', emoji: '🟠', description: 'Clearly visible thinning or scalp showing', value: 7, tags: ['moderate-loss'] },
      { id: 'advanced', label: 'Advanced', emoji: '🔴', description: 'Large thin areas or bald spots', value: 10, tags: ['severe-loss', 'urgent'] },
      { id: 'severe', label: 'Severe', emoji: '⚫', description: 'Very little hair remaining in affected areas', value: 10, tags: ['severe-loss', 'urgent', 'rapid-loss'] },
    ],
  },
  // Q2: Duration - builds urgency narrative
  {
    id: 'loss-duration',
    type: 'single',
    question: 'How long have you been losing hair?',
    subtitle: 'Timing matters. Earlier treatment = better results.',
    icon: <Clock className="w-6 h-6" />,
    options: [
      { id: 'under-1', label: 'Less than 1 year', value: 3, tags: ['recent-onset'] },
      { id: '1-3-years', label: '1-3 years', value: 5, tags: ['moderate-duration'] },
      { id: '3-5-years', label: '3-5 years', value: 7, tags: ['long-duration'] },
      { id: '5-plus', label: '5-10 years', value: 9, tags: ['long-duration', 'urgent'] },
      { id: 'over-10', label: '10+ years', value: 10, tags: ['long-duration', 'severe-loss', 'urgent'] },
    ],
  },
  // Q3: Shedding rate - emotional trigger
  {
    id: 'shedding-rate',
    type: 'single',
    question: 'How much hair do you find in the shower or on your pillow?',
    subtitle: 'This tells us how active your hair loss currently is.',
    icon: <Droplets className="w-6 h-6" />,
    options: [
      { id: 'minimal', label: 'A few strands', emoji: '✓', description: 'Seems normal', value: 0, tags: ['low-shedding'] },
      { id: 'noticeable', label: 'Noticeable amount daily', emoji: '⚠️', description: '20-50 hairs in shower', value: 5, tags: ['moderate-shedding', 'excessive-shedding'] },
      { id: 'heavy', label: 'Heavy shedding', emoji: '🚨', description: '50-100+ hairs, drain clogs', value: 8, tags: ['excessive-shedding', 'urgent', 'rapid-loss'] },
      { id: 'clumps', label: 'Clumps coming out', emoji: '🆘', description: "Alarming amounts, can't ignore it", value: 10, tags: ['excessive-shedding', 'urgent', 'rapid-loss', 'severe-loss'] },
    ],
  },
  // Q4: Family history - validates concern
  {
    id: 'family-history',
    type: 'single',
    question: 'Does hair loss run in your family?',
    subtitle: 'Genetics account for 80% of male pattern baldness.',
    icon: <TrendingDown className="w-6 h-6" />,
    options: [
      { id: 'father', label: 'Yes, my father', emoji: '👨', value: 8, tags: ['genetic', 'hereditary'] },
      { id: 'mother-side', label: "Yes, mother's side", emoji: '👩', description: 'Maternal grandfather or uncles', value: 9, tags: ['genetic', 'hereditary', 'maternal'] },
      { id: 'both', label: 'Both sides', emoji: '👨‍👩‍👦', value: 10, tags: ['genetic', 'hereditary', 'urgent'] },
      { id: 'no', label: 'No family history', emoji: '✓', value: 2, tags: ['non-genetic'] },
      { id: 'unsure', label: 'Not sure', emoji: '❓', value: 5, tags: ['unknown-genetics'] },
    ],
  },
  // Q5: Previous treatments - identify switchers
  {
    id: 'previous-treatments',
    type: 'multiple',
    question: "What have you tried before?",
    subtitle: 'Select all that apply.',
    icon: <Package className="w-6 h-6" />,
    options: [
      { id: 'minoxidil', label: 'Minoxidil (Rogaine)', emoji: '💊', description: 'Topical liquid or foam', value: 5, tags: ['tried-minoxidil', 'tried-otc'] },
      { id: 'finasteride', label: 'Finasteride (Propecia)', emoji: '💉', description: 'Prescription pill', value: 5, tags: ['tried-finasteride', 'side-effects'] },
      { id: 'prp', label: 'PRP Injections', emoji: '🏥', description: 'Platelet-rich plasma therapy', value: 7, tags: ['tried-prp', 'tried-expensive'] },
      { id: 'supplements', label: 'Biotin / Supplements', emoji: '🧴', description: 'Vitamins, biotin, collagen pills', value: 3, tags: ['tried-supplements', 'tried-otc'] },
      { id: 'shampoos', label: 'Hair loss shampoos', emoji: '🚿', description: 'Nioxin, Kerastase, etc.', value: 3, tags: ['tried-serums', 'tried-otc'] },
      { id: 'nothing', label: 'Nothing yet', emoji: '🆕', description: 'This would be my first treatment', value: 2, tags: ['tried-nothing'] },
    ],
  },
  // Q6: Hair loss area - now user is invested
  {
    id: 'hair-loss-area',
    type: 'single',
    question: 'Where are you losing hair the most?',
    subtitle: 'This helps us target the right follicle zones.',
    icon: <TrendingDown className="w-6 h-6" />,
    options: [
      { id: 'crown', label: 'Crown / Top of head', emoji: '🔝', description: 'Thinning or bald spot on top', value: 10, tags: ['crown-thinning', 'moderate-loss'] },
      { id: 'temples', label: 'Temples / Hairline', emoji: '↗️', description: 'Receding at the front and sides', value: 10, tags: ['receding-hairline', 'temples'] },
      { id: 'all-over', label: 'All over (diffuse)', emoji: '🔄', description: 'General thinning everywhere', value: 8, tags: ['diffuse-thinning', 'moderate-loss'] },
      { id: 'front', label: 'Front hairline only', emoji: '⬆️', description: 'Hairline moving backward', value: 9, tags: ['receding-hairline'] },
      { id: 'multiple', label: 'Multiple areas', emoji: '📍', description: 'Crown + temples or more', value: 10, tags: ['severe-loss', 'crown-thinning', 'temples', 'urgent'] },
    ],
  },
  // Q7: Hair texture - specific but relevant
  {
    id: 'hair-texture',
    type: 'single',
    question: "What's your natural hair texture?",
    subtitle: 'This affects how deep the treatment needs to penetrate.',
    icon: <Scissors className="w-6 h-6" />,
    options: [
      { id: 'fine-straight', label: 'Fine & Straight', emoji: '〰️', description: 'Thin individual strands, lies flat', value: 5, tags: ['fine-hair', 'thin-texture'] },
      { id: 'medium-straight', label: 'Medium & Straight', emoji: '➖', description: 'Average thickness, manageable', value: 3, tags: ['medium-hair'] },
      { id: 'thick-straight', label: 'Thick & Straight', emoji: '━', description: 'Coarse individual strands', value: 4, tags: ['coarse-hair', 'thick-texture'] },
      { id: 'wavy', label: 'Wavy', emoji: '🌊', description: 'Loose S-shaped waves', value: 4, tags: ['wavy-hair'] },
      { id: 'curly', label: 'Curly / Coily', emoji: '🔄', description: 'Tight curls or coils', value: 5, tags: ['curly-hair', 'coarse-hair'] },
    ],
  },
  // Q8: Lifestyle factors
  {
    id: 'lifestyle-factors',
    type: 'multiple',
    question: 'Which of these apply to you?',
    subtitle: 'Lifestyle factors that accelerate hair loss.',
    icon: <Zap className="w-6 h-6" />,
    options: [
      { id: 'high-stress', label: 'High stress levels', emoji: '😰', description: 'Work, personal, or financial stress', value: 5, tags: ['stress-related', 'lifestyle'] },
      { id: 'poor-sleep', label: 'Poor sleep quality', emoji: '😴', description: 'Less than 6 hours or restless', value: 4, tags: ['lifestyle', 'sleep-issues'] },
      { id: 'poor-diet', label: 'Inconsistent diet', emoji: '🍔', description: 'Fast food, skipping meals', value: 3, tags: ['lifestyle', 'nutritional'] },
      { id: 'smoking', label: 'Smoking or vaping', emoji: '🚬', description: 'Reduces blood flow to follicles', value: 5, tags: ['lifestyle', 'smoking'] },
      { id: 'medication', label: 'Taking medications', emoji: '💊', description: 'Some meds cause hair loss', value: 4, tags: ['medication-related'] },
      { id: 'none', label: 'None of these', emoji: '✅', value: 0, tags: ['healthy-lifestyle'] },
    ],
  },
  // Q9: Confidence impact - emotional close
  {
    id: 'confidence-impact',
    type: 'single',
    question: 'How much does hair loss affect your confidence?',
    subtitle: "Be honest. This is just between us.",
    icon: <AlertTriangle className="w-6 h-6" />,
    options: [
      { id: 'devastating', label: "It's devastating. I think about it daily.", emoji: '😞', value: 10, tags: ['high-impact', 'urgent'] },
      { id: 'significant', label: 'Significant. I avoid certain situations.', emoji: '😟', description: 'Avoid photos, swimming, wind', value: 8, tags: ['high-impact'] },
      { id: 'moderate', label: 'Moderate. It bothers me but I cope.', emoji: '😐', value: 5, tags: ['moderate-impact'] },
      { id: 'minor', label: 'Minor. Just want to stay ahead of it.', emoji: '🙂', value: 3, tags: ['preventive'] },
    ],
  },
  // Q10: Commitment - buying signal
  {
    id: 'commitment',
    type: 'single',
    question: 'How committed are you to solving this?',
    subtitle: 'Colagea works best with consistent daily use.',
    icon: <Zap className="w-6 h-6" />,
    options: [
      { id: 'very', label: "Very. I'm ready to start today.", emoji: '🔥', value: 10, tags: ['high-commitment', 'ready-to-buy'] },
      { id: 'committed', label: 'Committed, if I see proof it works.', emoji: '💪', value: 7, tags: ['needs-proof'] },
      { id: 'exploring', label: 'Exploring options. Still researching.', emoji: '🔍', value: 4, tags: ['researching'] },
      { id: 'skeptical', label: "Skeptical. I've been burned before.", emoji: '🤔', value: 5, tags: ['skeptical', 'tried-otc'] },
    ],
  },
];

// Profile definitions for Colagea
interface QuizProfile {
  id: string;
  name: string;
  slug: string;
  minScore: number;
  requiredTags: string[];
  dangerScore: number;
  description: string;
  recommendation: string;
  cureDuration: '1-month' | '2-month' | '4-month';
  cureQuantity: 1 | 2 | 4;
}

const profiles: QuizProfile[] = [
  {
    id: 'severe-urgent',
    name: 'Severe Hair Loss',
    slug: 'severe-hair-loss',
    minScore: 60,
    requiredTags: ['severe-loss'],
    dangerScore: 92,
    description: 'Your hair loss is advanced and actively progressing. Without immediate intervention, dormant follicles risk permanent shutdown.',
    recommendation: 'We strongly recommend the 4-Month Intensive Cure for maximum follicle reactivation.',
    cureDuration: '4-month',
    cureQuantity: 4,
  },
  {
    id: 'crown-moderate',
    name: 'Crown Thinning',
    slug: 'crown-thinning',
    minScore: 40,
    requiredTags: ['crown-thinning'],
    dangerScore: 75,
    description: 'Crown thinning responds exceptionally well to micro-infusion delivery. Your follicles are still recoverable.',
    recommendation: 'The 2-Month Cure targets crown follicles effectively. For persistent thinning, the 4-Month Cure provides deeper reactivation.',
    cureDuration: '2-month',
    cureQuantity: 2,
  },
  {
    id: 'receding-hairline',
    name: 'Receding Hairline',
    slug: 'receding-hairline',
    minScore: 35,
    requiredTags: ['receding-hairline'],
    dangerScore: 70,
    description: 'Hairline recession requires targeted delivery to the frontal follicles. This is exactly what the micro-needle cap is designed for.',
    recommendation: 'Start with the 2-Month Cure and focus application on the hairline zone. Most users see visible baby hair growth within 5-6 weeks.',
    cureDuration: '2-month',
    cureQuantity: 2,
  },
  {
    id: 'diffuse-thinning',
    name: 'Diffuse Thinning',
    slug: 'diffuse-thinning',
    minScore: 30,
    requiredTags: ['diffuse-thinning'],
    dangerScore: 65,
    description: 'Diffuse thinning affects the entire scalp and requires comprehensive treatment across all zones.',
    recommendation: 'The 4-Month Cure provides the sustained treatment needed for full-scalp coverage.',
    cureDuration: '4-month',
    cureQuantity: 4,
  },
  {
    id: 'early-stage',
    name: 'Early Stage Prevention',
    slug: 'early-prevention',
    minScore: 20,
    requiredTags: ['early-stage'],
    dangerScore: 45,
    description: "You're catching this early, which means your follicles are still highly responsive to treatment.",
    recommendation: 'The 1-Month Cure is perfect to start. You should see visible thickening within 4-5 weeks. Extend to 2 months for lasting results.',
    cureDuration: '1-month',
    cureQuantity: 1,
  },
  {
    id: 'treatment-switcher',
    name: 'Treatment Switcher',
    slug: 'treatment-switch',
    minScore: 25,
    requiredTags: ['tried-minoxidil'],
    dangerScore: 60,
    description: "You've tried minoxidil but didn't get the results you wanted. Colagea's micro-needle delivery reaches 10x deeper than topical solutions.",
    recommendation: 'The 2-Month Cure lets you see the difference vs. your previous treatment. No prescription, no side effects.',
    cureDuration: '2-month',
    cureQuantity: 2,
  },
  {
    id: 'general',
    name: 'Hair Regrowth Candidate',
    slug: 'hair-regrowth',
    minScore: 0,
    requiredTags: [],
    dangerScore: 50,
    description: "Based on your answers, Colagea's micro-infusion technology can help improve your hair density and slow further loss.",
    recommendation: 'Start with the 1-Month Cure to experience the micro-needle difference.',
    cureDuration: '1-month',
    cureQuantity: 1,
  },
];

// Loading messages
const loadingMessages = [
  { text: 'Analyzing your hair loss pattern...', detail: 'Mapping affected follicle zones' },
  { text: 'Evaluating treatment history...', detail: 'Identifying what works for your profile' },
  { text: 'Calculating your follicle health score...', detail: 'Assessing reactivation potential' },
  { text: 'Building your personalized cure plan...', detail: 'Matching you to the optimal protocol' },
  { text: 'Finalizing your results...', detail: 'Almost there!' },
];

// Cure pricing data
const curePricing: Record<string, { price: number; originalPrice: number; perMonth: number; savings: number; savingsPercent: number }> = {
  '1-month': { price: 55.90, originalPrice: 109.90, perMonth: 55.90, savings: 54.00, savingsPercent: 49 },
  '2-month': { price: 85.90, originalPrice: 199.90, perMonth: 42.95, savings: 114.00, savingsPercent: 57 },
  '4-month': { price: 129.90, originalPrice: 379.90, perMonth: 32.48, savings: 250.00, savingsPercent: 66 },
};

// ============================================
// SUB-COMPONENTS
// ============================================

function TrustHeader() {
  return (
    <div className="flex items-center justify-center gap-2 mb-6 py-3 border-b border-gray-100">
      <TrustpilotStars className="h-4" />
      <span className="text-sm text-[#666]">Trusted by <strong>782,000+</strong> Happy Customers</span>
    </div>
  );
}

function TestimonialCarousel({ userTags }: { userTags: string[] }) {
  const [offset, setOffset] = useState(0);
  
  const relevantTestimonials = userTags.length > 0
    ? testimonials.filter(t => t.tags.some(tag => userTags.includes(tag)))
    : testimonials;
  
  const displayTestimonials = relevantTestimonials.length > 0 ? relevantTestimonials : testimonials;
  const loopedTestimonials = [...displayTestimonials, ...displayTestimonials, ...displayTestimonials];
  
  useEffect(() => {
    const interval = setInterval(() => {
      setOffset(prev => prev + 0.5);
    }, 50);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="overflow-hidden mb-6 -mx-4">
      <div 
        className="flex gap-4 transition-transform"
        style={{ transform: `translateX(-${offset % (displayTestimonials.length * 280)}px)` }}
      >
        {loopedTestimonials.map((testimonial, idx) => (
          <div 
            key={idx}
            className="flex-shrink-0 w-[260px] bg-white rounded-lg border border-gray-100 p-3"
          >
            <div className="flex items-center gap-1 mb-1">
              <TrustpilotStars className="h-3" />
            </div>
            <p className="text-xs text-[#666] line-clamp-3 mb-2">"{testimonial.text}"</p>
            <p className="text-xs font-medium text-[#1a1a1a]">{testimonial.name}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// DANGER SCORE GAUGE COMPONENT
// ============================================
function DangerGauge({ score, animated }: { score: number; animated: boolean }) {
  const radius = 60;
  const strokeWidth = 12;
  const circumference = Math.PI * radius; // semicircle
  const fillLength = animated ? (score / 100) * circumference : 0;
  
  const getColor = (s: number) => {
    if (s >= 75) return '#ef4444'; // red
    if (s >= 50) return '#f59e0b'; // amber
    return '#22c55e'; // green
  };
  
  const getLabel = (s: number) => {
    if (s >= 80) return 'Critical';
    if (s >= 65) return 'High Risk';
    if (s >= 50) return 'Moderate Risk';
    return 'Low Risk';
  };

  return (
    <div className="flex flex-col items-center">
      <svg width="160" height="95" viewBox="0 0 160 95">
        {/* Background arc */}
        <path
          d="M 10 85 A 60 60 0 0 1 150 85"
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {/* Filled arc */}
        <path
          d="M 10 85 A 60 60 0 0 1 150 85"
          fill="none"
          stroke={getColor(score)}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${circumference}`}
          strokeDashoffset={circumference - fillLength}
          style={{ transition: 'stroke-dashoffset 1.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
        />
        {/* Score text */}
        <text x="80" y="75" textAnchor="middle" className="text-3xl font-bold" fill={getColor(score)} style={{ fontSize: '28px', fontWeight: 800 }}>
          {animated ? score : 0}
        </text>
        <text x="80" y="92" textAnchor="middle" fill="#666" style={{ fontSize: '11px' }}>
          / 100
        </text>
      </svg>
      <span className="text-sm font-bold mt-1" style={{ color: getColor(score) }}>
        {getLabel(score)}
      </span>
    </div>
  );
}

// ============================================
// IN-QUIZ RESULT PAGE COMPONENT
// ============================================
interface ResultPageProps {
  profile: QuizProfile;
  score: number;
  tags: string[];
  labels: string[];
  onStartCure: (quantity: 1 | 2 | 4) => void;
  onClose: () => void;
}

function QuizResultPage({ profile, score, tags, labels, onStartCure, onClose }: ResultPageProps) {
  const [gaugeAnimated, setGaugeAnimated] = useState(false);
  const [sectionsVisible, setSectionsVisible] = useState(false);
  const [ctaVisible, setCtaVisible] = useState(false);
  
  useEffect(() => {
    // Staggered animation
    const t1 = setTimeout(() => setGaugeAnimated(true), 300);
    const t2 = setTimeout(() => setSectionsVisible(true), 800);
    const t3 = setTimeout(() => setCtaVisible(true), 1200);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  const pricing = curePricing[profile.cureDuration];
  const durationLabel = profile.cureDuration === '1-month' ? '1 Month' : profile.cureDuration === '2-month' ? '2 Months' : '4 Months';

  // Get relevant testimonial
  const relevantTestimonial = testimonials.find(t => 
    t.tags.some(tag => tags.includes(tag))
  ) || testimonials[0];

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
          <CheckCircle2 className="w-4 h-4" />
          Analysis Complete
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1a1a] mb-2" style={{ fontFamily: "'ABC Arizona Flare', serif" }}>
          Your Hair Loss Profile
        </h2>
        <p className="text-[#666] text-sm sm:text-base">
          Based on your answers, here's your personalized assessment.
        </p>
      </div>

      {/* Profile Tags */}
      {labels.length > 0 && (
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {labels.map((label, i) => (
            <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold">
              {label}
            </span>
          ))}
        </div>
      )}

      {/* Danger Score Gauge */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-4">
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <DangerGauge score={profile.dangerScore} animated={gaugeAnimated} />
          <div className="flex-1 text-center sm:text-left">
            <h3 className="text-lg font-bold text-[#1a1a1a] mb-1">{profile.name}</h3>
            <p className="text-sm text-[#666] leading-relaxed">{profile.description}</p>
          </div>
        </div>
      </div>

      {/* Recommendation Card */}
      <div 
        className={`bg-[#0a1628] rounded-2xl p-5 sm:p-6 mb-4 transition-all duration-700 ${
          sectionsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h4 className="text-white font-bold text-base mb-1">Your Recommended Treatment</h4>
            <p className="text-blue-200/80 text-sm leading-relaxed">{profile.recommendation}</p>
          </div>
        </div>

        {/* Recommended Cure Card */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
          <div className="flex items-center justify-between mb-3">
            <div>
              <span className="text-white font-bold text-lg">{durationLabel} Cure</span>
              <span className="ml-2 text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full font-semibold">
                SAVE {pricing.savingsPercent}%
              </span>
            </div>
            <div className="text-right">
              <span className="text-white font-bold text-xl">${pricing.price.toFixed(2)}</span>
              <span className="text-white/40 text-sm line-through ml-2">${pricing.originalPrice.toFixed(2)}</span>
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs text-blue-200/70">
            <span className="flex items-center gap-1"><Truck className="w-3 h-3" /> Free Shipping</span>
            <span className="flex items-center gap-1"><RotateCcw className="w-3 h-3" /> 60-Day Returns</span>
            <span className="flex items-center gap-1"><Package className="w-3 h-3" /> Free Welcome Pack</span>
          </div>
        </div>
      </div>

      {/* Testimonial */}
      <div 
        className={`bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 mb-4 transition-all duration-700 delay-200 ${
          sectionsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 text-sm font-bold text-[#666]">
            {relevantTestimonial.name.charAt(0)}
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <TrustpilotStars className="h-3.5" />
              <span className="text-xs text-[#999]">Verified Purchase</span>
            </div>
            <p className="text-sm text-[#555] leading-relaxed italic">"{relevantTestimonial.text}"</p>
            <p className="text-xs font-medium text-[#1a1a1a] mt-2">{relevantTestimonial.name}</p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div 
        className={`transition-all duration-700 delay-500 ${
          ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <button
          onClick={() => onStartCure(profile.cureQuantity)}
          className="w-full bg-[#007AFF] hover:bg-[#0066CC] text-white font-bold py-4 sm:py-5 rounded-full text-lg shadow-lg shadow-blue-500/25 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/30 flex items-center justify-center gap-2"
        >
          Start My {durationLabel} Cure
          <ArrowRight className="w-5 h-5" />
        </button>
        
        {/* Trust signals */}
        <div className="flex items-center justify-center gap-4 mt-4 text-xs text-[#999]">
          <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> Secure Checkout</span>
          <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> 60-Day Guarantee</span>
          <span className="flex items-center gap-1"><Truck className="w-3 h-3" /> Free Shipping</span>
        </div>

        {/* Skip link */}
        <button
          onClick={onClose}
          className="block mx-auto mt-4 text-sm text-[#999] hover:text-[#666] underline underline-offset-4 transition-colors"
        >
          Continue browsing
        </button>
      </div>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================
interface QuizModuleProps {
  onComplete?: (profile: QuizProfile, score: number, tags: string[]) => void;
  onStartCure?: (quantity: 1 | 2 | 4) => void;
  onClose?: () => void;
  onShowResults?: (profileSlug: string, tags: string[], labels: string[], cureQuantity: 1 | 2 | 4) => void;
}

// localStorage key for quiz progress
const QUIZ_STORAGE_KEY = 'colagea_quiz_progress';
const QUIZ_STORAGE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

function saveQuizProgress(step: number, answers: Record<string, string[]>) {
  try {
    localStorage.setItem(QUIZ_STORAGE_KEY, JSON.stringify({
      step,
      answers,
      timestamp: Date.now(),
    }));
  } catch { /* quota exceeded or private browsing */ }
}

function loadQuizProgress(): { step: number; answers: Record<string, string[]> } | null {
  try {
    const raw = localStorage.getItem(QUIZ_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Date.now() - data.timestamp > QUIZ_STORAGE_EXPIRY) {
      localStorage.removeItem(QUIZ_STORAGE_KEY);
      return null;
    }
    return { step: data.step, answers: data.answers };
  } catch {
    return null;
  }
}

function clearQuizProgress() {
  try { localStorage.removeItem(QUIZ_STORAGE_KEY); } catch { /* noop */ }
}

// Haptic feedback helper (vibrate API for mobile)
function triggerHaptic(pattern: number | number[] = 10) {
  if (typeof navigator !== 'undefined' && navigator.vibrate) {
    navigator.vibrate(pattern);
  }
}

export default function QuizModule({ onComplete, onStartCure, onClose, onShowResults }: QuizModuleProps) {
  // Restore saved progress
  const savedProgress = useRef(loadQuizProgress());
  const [currentStep, setCurrentStep] = useState(savedProgress.current?.step ?? 0);
  const [answers, setAnswers] = useState<Record<string, string[]>>(savedProgress.current?.answers ?? {});
  const [showResumeBar, setShowResumeBar] = useState(!!savedProgress.current && savedProgress.current.step > 0);
  const [email, setEmail] = useState('');
  const [showLoading, setShowLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState(loadingMessages[0].text);
  const [loadingDetail, setLoadingDetail] = useState(loadingMessages[0].detail);
  const [loadingComplete, setLoadingComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [showBeliefCheckpoint, setShowBeliefCheckpoint] = useState(false);
  const [currentCheckpoint, setCurrentCheckpoint] = useState<{ title: string; body: string; stat?: string } | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [resultProfile, setResultProfile] = useState<QuizProfile | null>(null);
  const [resultScore, setResultScore] = useState(0);
  const [resultTags, setResultTags] = useState<string[]>([]);
  const [resultLabels, setResultLabels] = useState<string[]>([]);
  const [tappedOptionId, setTappedOptionId] = useState<string | null>(null);

  // Save progress whenever step or answers change
  useEffect(() => {
    if (currentStep > 0 || Object.keys(answers).length > 0) {
      saveQuizProgress(currentStep, answers);
    }
  }, [currentStep, answers]);

  // Clear progress when results are shown
  useEffect(() => {
    if (showResults || showLoading) {
      clearQuizProgress();
    }
  }, [showResults, showLoading]);

  // Handle restart from beginning
  const handleRestartQuiz = () => {
    clearQuizProgress();
    setCurrentStep(0);
    setAnswers({});
    setShowResumeBar(false);
  };
  

  const currentQuestion = quizQuestions[currentStep];
  const progress = ((currentStep + 1) / quizQuestions.length) * 100;
  const isLastQuestion = currentStep === quizQuestions.length - 1;

  const collectTags = useCallback(() => {
    const tags: string[] = [];
    Object.entries(answers).forEach(([questionId, selectedIds]) => {
      const question = quizQuestions.find(q => q.id === questionId);
      if (!question) return;
      selectedIds.forEach(selectedId => {
        const option = question.options.find(opt => opt.id === selectedId);
        if (option?.tags) {
          tags.push(...option.tags);
        }
      });
    });
    return tags;
  }, [answers]);

  const handleAnswer = (optionId: string) => {
    // Visual feedback: set tapped option for animation
    setTappedOptionId(optionId);
    triggerHaptic(10);
    setTimeout(() => setTappedOptionId(null), 300);

    if (currentQuestion.type === 'multiple') {
      const current = answers[currentQuestion.id] || [];
      const isDeselect = current.includes(optionId);
      const newAnswers = isDeselect
        ? current.filter(id => id !== optionId)
        : [...current, optionId];
      // Slightly stronger haptic on multi-select toggle
      if (!isDeselect) triggerHaptic([10, 30, 10]);
      setAnswers({ ...answers, [currentQuestion.id]: newAnswers });
    } else {
      const newAnswers = { ...answers, [currentQuestion.id]: [optionId] };
      setAnswers(newAnswers);
      
      trackQuizProgress(currentStep + 1, currentQuestion.id, quizQuestions.length, optionId);
      
      setTimeout(() => {
        const checkpoint = beliefCheckpoints.find(cp => cp.triggeredAfter === currentQuestion.id);
        if (checkpoint) {
          const message = checkpoint.getMessage(newAnswers);
          if (message) {
            setCurrentCheckpoint(message);
            setShowBeliefCheckpoint(true);
            return;
          }
        }
        handleNextQuestion();
      }, 350);
    }
  };

  const handleContinueFromCheckpoint = () => {
    setShowBeliefCheckpoint(false);
    setCurrentCheckpoint(null);
    handleNextQuestion();
  };

  const handleNextQuestion = () => {
    if (isLastQuestion) {
      setShowLoading(true);
      startLoadingAnimation();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleNext = () => {
    if (currentQuestion.type === 'multiple') {
      const selectedAnswers = answers[currentQuestion.id] || [];
      trackQuizProgress(currentStep + 1, currentQuestion.id, quizQuestions.length, selectedAnswers.join(','));
    }
    const checkpoint = beliefCheckpoints.find(cp => cp.triggeredAfter === currentQuestion.id);
    if (checkpoint) {
      const message = checkpoint.getMessage(answers);
      if (message) {
        setCurrentCheckpoint(message);
        setShowBeliefCheckpoint(true);
        return;
      }
    }
    handleNextQuestion();
  };

  const handleBack = () => {
    if (showLoading) {
      setShowLoading(false);
      setLoadingProgress(0);
      setLoadingComplete(false);
    } else if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const startLoadingAnimation = () => {
    setLoadingProgress(0);
    setLoadingComplete(false);
    setAllTags(collectTags());
    
    const stops = [
      { progress: 50, duration: 2000, messageIdx: 0 },
      { progress: 75, duration: 2500, messageIdx: 1 },
      { progress: 85, duration: 2000, messageIdx: 2 },
      { progress: 95, duration: 2000, messageIdx: 3 },
      { progress: 97, duration: 1000, messageIdx: 4 },
      { progress: 100, duration: 500, messageIdx: 4 },
    ];
    
    let currentStopIndex = 0;
    let currentStopStartTime = Date.now();
    
    const animate = () => {
      const now = Date.now();
      const currentStop = stops[currentStopIndex];
      const prevProgress = currentStopIndex === 0 ? 0 : stops[currentStopIndex - 1].progress;
      const elapsedInStop = now - currentStopStartTime;
      const stopProgress = Math.min(elapsedInStop / currentStop.duration, 1);
      
      const easedProgress = 1 - Math.pow(1 - stopProgress, 2);
      const displayProgress = prevProgress + (currentStop.progress - prevProgress) * easedProgress;
      
      setLoadingProgress(displayProgress);
      
      if (currentStop.messageIdx < loadingMessages.length) {
        setLoadingMessage(loadingMessages[currentStop.messageIdx].text);
        setLoadingDetail(loadingMessages[currentStop.messageIdx].detail);
      }
      
      if (stopProgress >= 1) {
        if (currentStopIndex < stops.length - 1) {
          currentStopIndex++;
          currentStopStartTime = now;
          requestAnimationFrame(animate);
        } else {
          setLoadingProgress(100);
          setLoadingComplete(true);
        }
      } else {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  };

  const calculateResults = useCallback(() => {
    let totalScore = 0;
    const collectedTags: string[] = [];

    Object.entries(answers).forEach(([questionId, selectedIds]) => {
      const question = quizQuestions.find(q => q.id === questionId);
      if (!question) return;

      selectedIds.forEach(selectedId => {
        const option = question.options.find(opt => opt.id === selectedId);
        if (option) {
          totalScore += option.value;
          if (option.tags) {
            collectedTags.push(...option.tags);
          }
        }
      });
    });

    const matchedProfile = profiles.find(profile => {
      const hasRequiredTags = profile.requiredTags.every(tag => collectedTags.includes(tag));
      const meetsMinScore = totalScore >= profile.minScore;
      return hasRequiredTags && meetsMinScore;
    }) || profiles[profiles.length - 1];

    return { profile: matchedProfile, score: totalScore, tags: collectedTags };
  }, [answers]);

  const handleSubmitEmail = async () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    
    trackQuizEmailCapture();
    
    await new Promise(resolve => setTimeout(resolve, 800));

    const { profile, score, tags } = calculateResults();
    const displayLabels = getDisplayLabels(tags);
    
    setResultProfile(profile);
    setResultScore(score);
    setResultTags(tags);
    setResultLabels(displayLabels);
    setAllTags(tags);

    trackQuizComplete(profile.slug, score, tags, displayLabels);

    if (onComplete) {
      onComplete(profile, score, tags);
    }

    // If onShowResults provided, close modal and show results on PDP page
    if (onShowResults) {
      setShowLoading(false);
      setIsSubmitting(false);
      onShowResults(profile.slug, tags, displayLabels, profile.cureQuantity);
      return;
    }
    // Fallback: show in-quiz result page
    setShowLoading(false);
    setShowResults(true);
    setIsSubmitting(false);
  };

  const handleSkipEmail = () => {
    const { profile, score, tags } = calculateResults();
    const displayLabels = getDisplayLabels(tags);
    
    setResultProfile(profile);
    setResultScore(score);
    setResultTags(tags);
    setResultLabels(displayLabels);
    setAllTags(tags);

    trackQuizComplete(profile.slug, score, tags, displayLabels);

    if (onComplete) {
      onComplete(profile, score, tags);
    }

    // If onShowResults provided, close modal and show results on PDP page
    if (onShowResults) {
      setShowLoading(false);
      onShowResults(profile.slug, tags, displayLabels, profile.cureQuantity);
      return;
    }
    setShowLoading(false);
    setShowResults(true);
  };

  const handleStartCure = (quantity: 1 | 2 | 4) => {
    if (onStartCure) {
      onStartCure(quantity);
    }
  };

  const handleCloseResults = () => {
    if (onClose) {
      onClose();
    }
  };

  // Scroll to top of modal container when transitioning between screens
  const containerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // When showing results, scroll the modal container to top
    if (showResults || showLoading || showBeliefCheckpoint) {
      // Find the closest scrollable parent (the QuizModal's overflow-y-auto div)
      const scrollParent = containerRef.current?.closest('[class*="overflow-y-auto"]');
      if (scrollParent) {
        scrollParent.scrollTop = 0;
      }
    }
  }, [showResults, showLoading, showBeliefCheckpoint, currentStep]);

  // ============================================
  // RENDER: In-Quiz Result Page
  // ============================================
  if (showResults && resultProfile) {
    return (
      <div ref={containerRef}>
        <QuizResultPage
          profile={resultProfile}
          score={resultScore}
          tags={resultTags}
          labels={resultLabels}
          onStartCure={handleStartCure}
          onClose={handleCloseResults}
        />
      </div>
    );
  }

  // ============================================
  // RENDER: Belief Checkpoint Screen
  // ============================================
  if (showBeliefCheckpoint && currentCheckpoint) {
    return (
      <div ref={containerRef} className="w-full max-w-2xl mx-auto">
        <TrustHeader />
        
        <div className="text-center py-8 animate-fadeIn">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-600" />
          </div>
          
          <h2 className="text-2xl font-bold text-[#1a1a1a] mb-3">
            {currentCheckpoint.title}
          </h2>
          
          <p className="text-[#666] mb-4 max-w-md mx-auto">
            {currentCheckpoint.body}
          </p>
          
          {currentCheckpoint.stat && (
            <div className="inline-block bg-red-50 border border-red-200 rounded-lg px-4 py-2 mb-6">
              <p className="text-sm font-medium text-red-700">
                {currentCheckpoint.stat}
              </p>
            </div>
          )}
          
          <Button
            onClick={handleContinueFromCheckpoint}
            className="bg-[#007AFF] hover:bg-[#0066CC] text-white px-8 py-3 text-lg font-semibold rounded-full"
          >
            Continue
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Loading screen with email form
  // ============================================
  if (showLoading) {
    return (
      <div ref={containerRef} className="w-full max-w-2xl mx-auto">
        <TrustHeader />

        <div className="text-center mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-[#1a1a1a] mb-2">
            {loadingComplete ? 'Your Hair Profile Is Ready!' : 'Analyzing Your Hair Loss Profile'}
          </h2>
          <p className="text-[#666] transition-all duration-300">
            {loadingComplete ? 'Enter your email to unlock your personalized treatment plan' : loadingMessage}
          </p>
          {!loadingComplete && (
            <p className="text-xs text-[#999] mt-1">{loadingDetail}</p>
          )}
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden mb-2">
            <div
              className="h-full bg-gradient-to-r from-[#007AFF] to-[#0066CC]"
              style={{ width: `${loadingProgress}%` }}
            />
          </div>
          <div className="text-center text-sm text-[#666]">
            {Math.round(loadingProgress)}% complete
          </div>
        </div>

        {/* Testimonial Carousel */}
        <TestimonialCarousel userTags={allTags} />

        {/* Email Form */}
        <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 mb-4">
          <div className="flex items-center gap-2 mb-3 text-sm text-[#666]">
            <Shield className="w-4 h-4" />
            <span>No spam, ever. Unsubscribe anytime.</span>
          </div>
          
          <div className="space-y-3">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadingComplete && handleSubmitEmail()}
              className="h-11 sm:h-12"
            />
            <button
              onClick={handleSubmitEmail}
              disabled={!loadingComplete || isSubmitting}
              className={`w-full font-semibold py-4 rounded-full text-lg transition-all duration-500 flex items-center justify-center gap-2 ${
                loadingComplete 
                  ? 'bg-[#007AFF] hover:bg-[#0066CC] text-white shadow-lg shadow-blue-500/25' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Unlocking...
                </>
              ) : loadingComplete ? (
                <>
                  <Lock className="w-4 h-4" />
                  See My Treatment Plan
                </>
              ) : (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Analyzing Your Profile...
                </>
              )}
            </button>
          </div>
        </div>

        {/* What you'll get */}
        <div className="flex items-center justify-center gap-4 text-xs text-[#666]">
          <span className="flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
            Follicle health score
          </span>
          <span className="flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
            Personalized cure plan
          </span>
          <span className="flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
            Exclusive discount
          </span>
        </div>

        {/* Skip / Back buttons */}
        <div className="flex items-center justify-between mt-4">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-sm text-[#666] hover:text-[#1a1a1a]"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to quiz
          </button>
          {loadingComplete && (
            <button
              onClick={handleSkipEmail}
              className="text-sm text-[#999] hover:text-[#666] underline underline-offset-4"
            >
              Skip, show my results
            </button>
          )}
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Quiz questions screen
  // ============================================
  return (
    <div ref={containerRef} className="w-full max-w-2xl mx-auto">
      <TrustHeader />

      {/* Resume bar - shown when returning to a saved quiz */}
      {showResumeBar && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center justify-between animate-fadeIn">
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <RotateCcw className="w-4 h-4" />
            <span>Welcome back! Continuing from question {currentStep + 1}.</span>
          </div>
          <button
            onClick={handleRestartQuiz}
            className="text-xs text-blue-500 hover:text-blue-700 underline underline-offset-2 whitespace-nowrap ml-2"
          >
            Start over
          </button>
        </div>
      )}

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-[#666]">
            Question {currentStep + 1} of {quizQuestions.length}
          </span>
          <span className="text-sm font-medium text-[#666]">{Math.round(progress)}%</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#007AFF] transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question Card */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6 mb-6">
        <div className="flex items-start gap-2 sm:gap-3 mb-4">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-blue-50 flex items-center justify-center text-[#007AFF] flex-shrink-0">
            {currentQuestion.icon}
          </div>
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-[#1a1a1a]">{currentQuestion.question}</h2>
            {currentQuestion.subtitle && (
              <p className="text-sm text-[#666] mt-1">{currentQuestion.subtitle}</p>
            )}
          </div>
        </div>

        {/* Options */}
        <div className="space-y-2 sm:space-y-3">
          {currentQuestion.options.map((option) => {
            const isSelected = (answers[currentQuestion.id] || []).includes(option.id);
            return (
              <button
                key={option.id}
                onClick={() => handleAnswer(option.id)}
                className={`w-full text-left p-3 sm:p-4 rounded-xl border-2 transition-all duration-200 ${
                  isSelected
                    ? 'border-[#007AFF] bg-blue-50/50 shadow-sm shadow-blue-100'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                } ${
                  tappedOptionId === option.id
                    ? 'scale-[0.97] transition-transform duration-100'
                    : 'scale-100'
                }`}
                style={{ transform: tappedOptionId === option.id ? 'scale(0.97)' : 'scale(1)', transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1), border-color 200ms, background-color 200ms, box-shadow 200ms' }}
              >
                <div className="flex items-center gap-3">
                  {option.emoji && (
                    <span className={`text-lg flex-shrink-0 transition-transform duration-200 ${
                      tappedOptionId === option.id ? 'scale-125' : 'scale-100'
                    }`}>{option.emoji}</span>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`font-medium transition-colors duration-200 ${isSelected ? 'text-[#007AFF]' : 'text-[#1a1a1a]'}`}>
                        {option.label}
                      </span>
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ml-2 transition-all duration-200 ${
                        isSelected
                          ? 'bg-[#007AFF] scale-100 opacity-100'
                          : 'bg-gray-200 scale-75 opacity-0'
                      }`}>
                        <CheckCircle2 className="w-3 h-3 text-white" />
                      </div>
                    </div>
                    {option.description && (
                      <p className="text-xs text-[#999] mt-0.5">{option.description}</p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Navigation */}
      <div className={`flex items-center justify-between ${
        currentQuestion.type === 'multiple'
          ? 'sticky bottom-0 bg-white/95 backdrop-blur-sm py-3 -mx-4 px-4 sm:-mx-6 sm:px-6 md:-mx-8 md:px-8 border-t border-gray-100 mt-4 z-10'
          : ''
      }`}>
        <button
          onClick={handleBack}
          disabled={currentStep === 0}
          className={`flex items-center gap-2 text-sm ${
            currentStep === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-[#666] hover:text-[#1a1a1a]'
          }`}
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </button>

        {currentQuestion.type === 'multiple' && (
          <Button
            onClick={handleNext}
            disabled={!(answers[currentQuestion.id]?.length > 0)}
            className="bg-[#007AFF] hover:bg-[#0066CC] text-white rounded-full px-6 shadow-lg"
          >
            Continue
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}
